import Configuration from '../Configuration';

export interface IHttpResponse<T> extends Response {
  parsedBody?: T;
  errorText?: string;
}

export class Service {
  protected config: Configuration;

  constructor() {
    this.config = new Configuration();
  }

  private request = <T>(
    request: RequestInfo,
    headers: Record<string, string>,
    json: boolean = true,
  ): Promise<IHttpResponse<T>> => {
    let response: IHttpResponse<T>;
    const req = request as Request;
    if (json) {
      req.headers.delete('Content-Type');
      req.headers.append('Content-Type', 'application/json');
    }

    for (const header in headers) {
      req.headers.append(header, headers[header]);
    }

    return new Promise<IHttpResponse<T>>((resolve, reject) => {
      fetch(request)
        .then(res => {
          response = res;
          if (!res.ok) {
            if ([401, 403, 404].indexOf(res.status) > -1) {
              res.text().then(err => {
                response.errorText = err;
              });

              reject(this.getResponseError(res));
              return;
            }
            res.json()
              .then(obj => {
                if (Object.prototype.hasOwnProperty.call(obj, 'message')) {
                  response.errorText = obj.message;
                } else {
                  response.errorText = JSON.stringify(obj);
                }
              });

            reject(this.getResponseError(res));
            return;
          }
          return res.json();
        })
        .then(body => {
          response.parsedBody = body;
          resolve(response);
        });
    });
  }

  public get = async <T>(
    path: string,
    headers: Record<string, string> = {},
    json: boolean = true,
    args: RequestInit = {method: 'get'},
  ): Promise<IHttpResponse<T>> => {
    return await this.request<T>(new Request(path, args), headers, json);
  }

  public post = async <T>(
    path: string,
    body: any,
    headers: Record<string, string> = {},
    args: RequestInit = {method: 'post', body: JSON.stringify(body)},
  ): Promise<IHttpResponse<T>> => {
    return await this.request<T>(new Request(path, args), headers);
  }

  public postForm = async <T>(
    path: string,
    body: any,
    headers: Record<string, string> = {},
    args: RequestInit = {method: 'post', body},
  ): Promise<IHttpResponse<T>> => {
    return await this.request<T>(new Request(path, args), headers, false);
  }

  public put = async <T>(
    path: string,
    body: any,
    headers: Record<string, string> = {},
    args: RequestInit = {method: 'put', body: JSON.stringify(body)},
  ): Promise<IHttpResponse<T>> => {
    return await this.request<T>(new Request(path, args), headers);
  }

  public putForm = async <T>(
    path: string,
    body: any,
    headers: Record<string, string> = {},
    args: RequestInit = {method: 'put', body},
  ): Promise<IHttpResponse<T>> => {
    return await this.request<T>(new Request(path, args), headers, false);
  }

  public delete = async <T>(
    path: string,
    headers: Record<string, string> = {},
    json: boolean = true,
    args: RequestInit = {method: 'delete'},
  ): Promise<IHttpResponse<T>> => {
    return await this.request<T>(new Request(path, args), headers, json);
  }

  protected getResponseError(res: Response) {
    return new Error(`HTTP ${res.status} ${res.statusText}`);
  }

  protected handleResponseError(error: Error) {
    throw error;
  }

  protected handleError(err: any) {
    console.log(err.message);
  }
}
